import { Box, Button, Divider, Grid, IconButton, LinearProgress, Stack, Tooltip } from "@mzc-pdc/ui";
import {
    IconAllView,
    IconArrowDropDown,
    IconClose,
    IconMoreVert,
    IconRefresh,
    IconCancel,
    IconReply,
} from "@mzc-cloudplex/icons";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CardComments from "@features/comments/domain/card-comments";
import { alpha, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import theme from "@styles/theme";
import {
    createPrivateThread,
    deleteThread,
    getComments,
    getThreadMembers,
    getThreads,
    resetComments,
    updateIsLinkCommentUsed,
    updateSelectedThreadId,
    updateThread,
    updateThreadMembers,
} from "@modules/comments";
import { useInView } from "react-intersection-observer";
import SkeletonComment from "@features/comments/skeleton-comment";
import moment from "moment";
import "moment/locale/ko";
import "moment/locale/ja";
import queryParamsParse from "@components/params/queryParamsParse";
import {
    DialogCreateGroup,
    DialogDeleteGroup,
    DialogEditGroupMembers,
    DialogEditGroupName,
    GroupDescription,
    GroupedAvatar,
    GroupTitleWithIcon,
} from "@features/comments/groups";
import useDialog from "@hooks/useDialog";
import { Tabs } from "@components_v2/tabs/tabs";
import useActionPending from "@hooks/useActionPending";
import PopperPrivateThreads from "@features/comments/groups/popper-private-threads";
import { useToggle } from "@mzc-pdc/hooks";
import useCommentSelector from "@hooks/useCommentSelector";
import { UPLOAD_LOCATION } from "@constants";
import { setNotification } from "@modules/notification";
import { getProjectUsersAPI } from "@modules/apis/user-management-v2";
import useCheckPermission from "@hooks/useCheckPermission";
import compareVersions, {
    VERSION_CM_12797_GET_USERS_WITH_ACTION_GROUP_IDS,
    VERSION_CM_13806_CUSTOM_MENU_MAIL_LINK,
} from "@cores/version";
import { getThreadAPI } from "@modules/apis/comments";
import useAbortController from "@hooks/useAbortController";
import calculateOpaqueColor from "@utils/calculate-opaque-color";
import buildMailConfig from "@utils/build-mail-config";
import { Menu } from "@components_v2/menu";

const ReplyContainer = ({
    domain,
    resourceId,
    hidden,
    isCustomMenu,
    isCms,
    userSiteAccess,
    projectId,
    addedUsers = [],
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const [prevTargetRef, prevInView] = useInView();
    const [nextTargetRef, nextInView] = useInView();
    const { dialog, openDialog, closeDialog } = useDialog();
    const { active: threadPopperActive, toggle: threadPopperToggle } = useToggle();
    const { getActionPending, setActionPending, disableActionPending } = useActionPending();
    const COMMENTS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Comments" });
    const { getNewController, abortAll, abort, handleApiResult } = useAbortController();

    const [mountPending, setMountPending] = useState(true);
    const [initPending, setInitPending] = useState(false);
    const [scrollYPosition, setScrollYPosition] = useState(0);
    const [prevScrollHeight, setPrevScrollHeight] = useState(0);
    const [hasScrolledToLinkComment, setHasScrolledToLinkComment] = useState(false);
    const [selectedTab, setSelectedTab] = useState("");
    const [selectedPrivateThreads, setSelectedPrivateThreads] = useState([]);
    const [defaultMembers, setDefaultMembers] = useState([]);

    const dateDividerRef = useRef(0);
    const timeOffsetRef = useRef(null);
    const scrollDivRef = useRef(null);
    const linkCommentRef = useRef(null);
    const groupsViewRef = useRef(null);
    const { comments, resourceData, isLinkCommentUsed, selectedThreadId, uploadingComments } = useCommentSelector({
        domain,
    });
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const uploadQueue = useSelector(({ uploadQueue }) => uploadQueue);

    const mailConfig = useMemo(
        () => buildMailConfig({ pathname: location.pathname, search: location.search, isCustomMenu }),
        [location.pathname, location.search, isCustomMenu, isCms],
    );

    const compareDates = (first, second) => {
        // 문자열을 Date 객체로 변환
        const firstDate = new Date(new Date(first).getTime() - timeOffsetRef.current);
        const secondDate = new Date(new Date(second).getTime() - timeOffsetRef.current);

        // 년, 월, 일 부분만 추출하여 문자열로 변환
        const formattedFirstDate = firstDate.toISOString().slice(0, 10);
        const formattedSecondDate = secondDate.toISOString().slice(0, 10);
        // 날짜 부분만 비교
        return formattedFirstDate !== formattedSecondDate;
    };

    const getLocalStoragePrivateThreads = useCallback((resourceData) => {
        const data = resourceData?.results?.[0]?.context;
        if (!data) return { domainResourceIds: {} };

        const { resourceId, domain, parentThread } = data;
        const domainResourceIds = JSON.parse(localStorage.getItem("privateThreads")) ?? {};
        const domainResourceId =
            domain === "COMMENT" ? `${parentThread.domain}_${parentThread.resourceId}` : `${domain}_${resourceId}`;
        const privateThread = Object.keys(domainResourceIds).find((key) => key === domainResourceId);
        const privateThreadIds = domainResourceIds[privateThread] ?? [];

        return { domainResourceIds, domainResourceId, privateThreadIds };
    }, []);

    const setLocalStoragePrivateThreads = useCallback(
        (privateThreadsArray, newThreadId = null) => {
            const { domainResourceIds, domainResourceId } = getLocalStoragePrivateThreads(resourceData);
            const id = newThreadId ?? domainResourceId;
            localStorage.setItem("privateThreads", JSON.stringify({ ...domainResourceIds, [id]: privateThreadsArray }));
        },
        [resourceData, getLocalStoragePrivateThreads],
    );

    const tabs = useMemo(() => {
        if (!resourceData || !resourceData?.results) return [];
        const threads = resourceData.results;
        const { privateThreadIds } = getLocalStoragePrivateThreads(resourceData);

        const defaultThread = {
            isPublic: true,
            value: "",
            name: "",
            label: <GroupTitleWithIcon isPublic={true} title={""} iconSize={16} fontVariant={"body3_500"} />,
        };

        const publicThreads = threads
            .filter((el) => el.type === "PUBLIC" && el.context?.domain !== "COMMENT")
            ?.map((el) => ({
                id: el.id,
                isPublic: true,
                value: el.id,
                name: el.name,
                label: <GroupTitleWithIcon isPublic={true} title={el.name} iconSize={16} fontVariant={"body3_500"} />,
                created: el.created,
            }))
            .reduce((acc, current) => {
                const existedTab = acc.find((item) => item.id === current.id);
                if (!existedTab) {
                    return acc.concat([current]);
                } else {
                    return acc;
                }
            }, []);

        const privateThreads = threads
            .filter((el) => privateThreadIds?.includes(el.id))
            ?.map((el) => ({
                id: el.id,
                isPublic: false,
                value: el.id,
                name: el.name,
                label: <GroupTitleWithIcon isPublic={false} title={el.name} iconSize={16} fontVariant={"body3_500"} />,
                icon: (
                    <IconButton
                        circled
                        size={"small"}
                        onClick={(e) => {
                            e.stopPropagation();
                            onHandleCheckPrivateThreads(el.id);
                        }}
                    >
                        <IconCancel size={12} htmlColor={theme.palette.greySecondary[500]} />
                    </IconButton>
                ),
                created: el.created,
            }));

        if (!publicThreads?.length) return [defaultThread, ...privateThreads];
        else return [...publicThreads, ...privateThreads];
    }, [selectedPrivateThreads, resourceData, selectedTab, getLocalStoragePrivateThreads]);

    const getSelectedTabInfo = useCallback(
        (tab) => {
            if (!tabs) return null;
            if (tab === "") return tabs.find((el) => el.isPublic);
            return tabs.find((el) => el.id === tab);
        },
        [tabs],
    );

    const onHandleRefresh = useCallback(async () => {
        if (!resourceData?.results?.length) return;
        const thread = getSelectedTabInfo(selectedTab);
        const threadId = thread?.id;
        if (!threadId) return;

        try {
            await setInitPending(true);
            await dispatch(resetComments());

            if (!thread.isPublic) {
                const signal = getNewController("threadMembers");
                const threadMembersResult = await dispatch(getThreadMembers(projectId, threadId, signal));
                handleApiResult(threadMembersResult);
            }

            const commentsSignal = getNewController("comments");
            const commentsResult = await dispatch(
                getComments(
                    projectId,
                    threadId,
                    {
                        limit: 10,
                        domain,
                        resourceId,
                    },
                    commentsSignal,
                ),
            );
            handleApiResult(commentsResult);
        } catch (error) {
            if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                console.log("취소된 요청으로 인해 작업이 중단됩니다.");
            } else {
                console.trace("API 요청 중 오류 발생:", error.message);
            }
        } finally {
            await setInitPending(false);
        }
    }, [resourceData?.results?.length, domain, resourceId, selectedTab, getSelectedTabInfo, projectId]);

    const makeDateDivider = useCallback((comment, index) => {
        let language = localStorage.getItem("i18nLng");
        if (language === "ko") language = "ko-KR";
        else if (language === "ja") language = "ja-JP";
        else language = "en-US";

        const date = moment(comment.created.at).calendar(null, {
            sameDay: `[${t(`common::label::Today`, "Today")}]`,
            lastDay: `[${t(`common::label::Yesterday`, "Yesterday")}]`,
            lastWeek: "dddd[,] MMMM Do",
            sameElse: `dddd[,] MMMM Do`,
        });

        return (
            <Grid
                item
                sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "sticky",
                    left: 0,
                    top: 0,
                    right: 0,
                    zIndex: 35 - index,
                    py: 0.5,
                    backgroundColor: calculateOpaqueColor(theme.palette.greySecondary["A100"], 0.48),
                }}
            >
                <Box sx={{ flex: 1, mr: 2 }}>
                    <Divider
                        flexItem
                        orientation={"horizontal"}
                        sx={{ borderColor: theme.palette.greySecondary["A100"] }}
                    />
                </Box>
                <Grid item>
                    <Button
                        variant={"outlined"}
                        size={"small"}
                        rounded
                        endIcon={<IconArrowDropDown size={16} />}
                        sx={{
                            height: "24px",
                            px: "12px",
                            color: theme.palette.greySecondary["A400"],
                            borderColor: alpha(theme.palette.greySecondary["A400"], 0.6),
                            "& .MuiButton-endIcon": {
                                marginLeft: "4px",
                            },
                        }}
                    >
                        <Typography variant={"body3_400"}>{date}</Typography>
                    </Button>
                </Grid>
                <Box sx={{ flex: 1, ml: 2 }}>
                    <Divider
                        flexItem
                        orientation={"horizontal"}
                        sx={{ borderColor: theme.palette.greySecondary["A100"] }}
                    />
                </Box>
            </Grid>
        );
    }, []);

    const linkParams = useMemo(() => {
        const { threadId, commentId, domain: linkDomain, isSubThread } = queryParamsParse(location.search);
        const isLinkFromEmail = threadId && linkDomain === domain;

        return { isLinkFromEmail, threadId, commentId, domain: linkDomain, isSubThread: isSubThread === "true" };
    }, [location.search]);

    const publicThreadId = useMemo(() => {
        if (!resourceData) return null;
        return resourceData.results?.find((el) => el.type === "PUBLIC")?.id;
    }, [resourceData]);

    const initCommentsFromLink = useCallback(async (freshResourceData) => {
        const { threadId: threadIdFromLink, commentId, isSubThread } = linkParams;
        const publicThreadId = freshResourceData?.results?.find((el) => el.type === "PUBLIC")?.id;

        setMountPending(true);

        let baseThreadId = threadIdFromLink; //Note: 댓글인 경우 스레드, 대댓글인 경우 해당 스레드의 댓글이 속한 스레드의 id
        let baseCommentId = commentId; //Note: 댓글인 경우 해당 댓글, 대댓글인 경우 해당 스레드가 속한 댓글의 id

        try {
            if (isSubThread) {
                try {
                    const customErrorMessage = true;
                    const signal = getNewController("getThread");
                    const threadResult = await getThreadAPI(
                        stage.endpoint,
                        projectId,
                        threadIdFromLink,
                        customErrorMessage,
                        signal,
                    );

                    handleApiResult(threadResult);

                    baseThreadId = threadResult?.data?.context?.parentThread?.id ?? null;
                    baseCommentId = threadResult?.data?.context?.resourceId ?? null;
                } catch (e) {
                    //Note: 삭제된 대댓글인 경우 아래의 threadDataFromLink가 없는 분기를 태우기 위해 baseThreadId를 null로 설정
                    if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                        console.log("getThread 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                        setMountPending(false);
                        return;
                    }
                    console.error("deleted thread:", threadIdFromLink);
                    baseThreadId = null;
                    baseCommentId = null;
                    setMountPending(false);
                }
            }
            const threadDataFromLink = freshResourceData?.results?.find((thread) => thread.id === baseThreadId);

            if (!threadDataFromLink) {
                setSelectedTab(publicThreadId);
                dispatch(
                    setNotification({
                        type: "info",
                        contents: t("common::msg::The private comment is deleted."),
                    }),
                );

                if (publicThreadId) {
                    try {
                        const signal = getNewController("comments");
                        const commentsResult = await dispatch(
                            getComments(
                                projectId,
                                publicThreadId,
                                {
                                    limit: 10,
                                    domain,
                                    resourceId,
                                },
                                signal,
                            ),
                        );
                        handleApiResult(commentsResult);
                    } catch (error) {
                        if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                            console.log("댓글 로드 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                            setMountPending(false);
                            return;
                        } else {
                            console.error("댓글 로드 중 오류 발생:", error);
                        }
                    }
                }
            } else {
                setSelectedTab(threadDataFromLink.type === "PUBLIC" ? publicThreadId : threadDataFromLink.id);

                if (threadDataFromLink.type !== "PUBLIC") {
                    await onHandleCheckPrivateThreads(threadDataFromLink.id, true, freshResourceData);

                    const signal = getNewController("threadMembers");
                    const threadMembersResult = await dispatch(
                        getThreadMembers(projectId, threadDataFromLink.id, signal),
                    );

                    try {
                        handleApiResult(threadMembersResult);
                    } catch (e) {
                        if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                            console.log("댓글 로드 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                            setMountPending(false);
                            return;
                        } else {
                            console.error("댓글 로드 중 오류 발생:", e);
                        }
                        throw e;
                    }
                }

                try {
                    const commentsSignal = getNewController("comments");
                    const commentsResult = await dispatch(
                        getComments(
                            projectId,
                            baseThreadId,
                            {
                                limit: 10,
                                domain,
                                resourceId,
                                ...(baseCommentId && {
                                    cursor: baseCommentId,
                                    orderType: "DESC",
                                }),
                            },
                            commentsSignal,
                        ),
                    );

                    handleApiResult(commentsResult);

                    if (baseCommentId) {
                        //Note: 스레드만 있는 상태에서 링크에 들어갈 경우 댓글은 이전 댓글 목록조회 1회만 하면된다.
                        const commentsAscSignal = getNewController("commentsAsc");
                        const commentsAscResult = await dispatch(
                            getComments(
                                projectId,
                                baseThreadId,
                                {
                                    limit: 10,
                                    domain,
                                    resourceId,
                                    cursor: baseCommentId,
                                    orderType: "ASC",
                                },
                                commentsAscSignal,
                            ),
                        );

                        handleApiResult(commentsAscResult);
                    }
                } catch (e) {
                    if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                        console.log("댓글 로드 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                        setMountPending(false);
                        return;
                    } else {
                        console.log(e);
                    }
                    throw e;
                }
            }
            dispatch(updateIsLinkCommentUsed(true));
        } catch (e) {
            console.log(e);
        } finally {
            setMountPending(false);
        }
    }, [projectId, resourceId, handleApiResult, getNewController, linkParams]);

    const initComments = useCallback(async () => {
        try {
            const signal = getNewController("comments");
            const commentsResult = await dispatch(
                getComments(
                    projectId,
                    publicThreadId,
                    {
                        limit: 10,
                        domain,
                        resourceId,
                    },
                    signal,
                ),
            );

            handleApiResult(commentsResult);
        } catch (e) {
            if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                console.log("댓글 로드 요청이 취소되었습니다. 이후 로직을 중단합니다.");
            }
            console.log(e);
        } finally {
            setMountPending(false);
        }
    }, [resourceData, linkParams, resourceId, domain, isLinkCommentUsed, publicThreadId]);

    const threadInfoWithMembers = useMemo(() => {
        const selectedThread = getSelectedTabInfo(selectedTab);
        let threadInfo = { ...selectedThread };
        if (!selectedThread?.id || selectedThread.isPublic) threadInfo.members = defaultMembers ?? [];
        else threadInfo.members = resourceData?.results?.find((el) => selectedThread?.id === el.id)?.members ?? [];
        return threadInfo;
    }, [selectedTab, resourceData, defaultMembers]);

    const onHandleChangeTab = (tab) => {
        if (initPending || mountPending || comments.prevPending || comments.nextPending) return;
        setSelectedTab(tab);
    };

    const onHandleCheckPrivateThreads = useCallback(
        (id, preventRemoval = false, overrideResourceData = null) => {
            const { privateThreadIds } = getLocalStoragePrivateThreads(overrideResourceData || resourceData);
            const selectedThread = getSelectedTabInfo(selectedTab);
            const isExist = privateThreadIds?.find((el) => el === id) !== undefined;

            let privateThreadsArray = [...privateThreadIds];
            if (isExist && !preventRemoval) {
                if (id === selectedThread?.id) setSelectedTab("");
                privateThreadsArray = privateThreadIds.filter((el) => el !== id);
            } else if (!isExist) {
                privateThreadsArray = [...privateThreadIds, id];
            }

            setSelectedPrivateThreads(privateThreadsArray);
            setLocalStoragePrivateThreads(privateThreadsArray);
        },
        [resourceData, selectedTab, getLocalStoragePrivateThreads, getSelectedTabInfo, setLocalStoragePrivateThreads],
    );

    const onCreatePrivateThread = async (props) => {
        try {
            setActionPending("createGroup");
            const data = {
                ...props,
                context: {
                    domain: domain,
                    resourceId,
                },
            };
            if (
                compareVersions(stage.version, VERSION_CM_13806_CUSTOM_MENU_MAIL_LINK) >= 0 &&
                (isCustomMenu || isCms)
            ) {
                data.mailConfig = mailConfig;
            }
            const signal = getNewController("createPrivateThread");
            const response = await dispatch(createPrivateThread(projectId, data, signal));

            handleApiResult(response);

            threadPopperToggle();
            const { privateThreadIds } = getLocalStoragePrivateThreads(resourceData);
            const newSelectedPrivateThreads = [...privateThreadIds, response?.data?.id];
            const domainResourceId = `${domain}_${resourceId}`;
            setLocalStoragePrivateThreads(newSelectedPrivateThreads, domainResourceId);
            setSelectedPrivateThreads(newSelectedPrivateThreads);
            setSelectedTab(response?.data?.id);
        } catch (e) {
            if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                console.log("비공개 스레드 생성 요청이 취소되었습니다. 이후 로직을 중단합니다.");
            } else {
                console.error("비공개 스레드 생성 중 오류 발생:", e);
            }
        } finally {
            disableActionPending("createGroup");
            closeDialog();
        }
    };

    const onUpdateThreadMembers = async (params) => {
        try {
            setActionPending("editGroupMembers");
            const threadId = getSelectedTabInfo(selectedTab)?.id;
            const signal = getNewController("updateThreadMembers");
            const result = await dispatch(updateThreadMembers(projectId, threadId, params, signal));

            handleApiResult(result);

            closeDialog();
        } catch (e) {
            if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                console.log("스레드 멤버 업데이트 요청이 취소되었습니다. 이후 로직을 중단합니다.");
            } else {
                console.error("스레드 멤버 업데이트 중 오류 발생:", e);
            }
        } finally {
            disableActionPending("editGroupMembers");
        }
    };

    const onDeletePrivateThread = async () => {
        try {
            setActionPending("deleteGroup");
            const threadId = getSelectedTabInfo(selectedTab)?.id;

            const signal = getNewController("deleteThread");
            const deleteResult = await dispatch(deleteThread(projectId, threadId, signal));
            handleApiResult(deleteResult, "스레드 삭제 요청이 취소되었습니다");

            const threadsSignal = getNewController("getThreads");
            const threadsResult = await dispatch(getThreads(projectId, { domain, resourceId }, threadsSignal));
            handleApiResult(threadsResult, "스레드 목록 요청이 취소되었습니다");

            const newSelectedPrivateThreads = selectedPrivateThreads.filter((el) => el.id !== threadId);
            setLocalStoragePrivateThreads(newSelectedPrivateThreads);
            closeDialog();
            setSelectedTab(publicThreadId ?? "");
        } catch (error) {
            console.error("스레드 삭제 처리 중 오류 발생:", error);
            if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                console.log("취소된 요청으로 인해 작업이 중단됩니다.");
            }
        } finally {
            disableActionPending("deleteGroup");
        }
    };

    const onUpdatePrivateThread = async (threadId, props) => {
        try {
            setActionPending("editGroupName");
            const signal = getNewController("updateThread");
            const result = await dispatch(updateThread(projectId, threadId, props, signal));

            handleApiResult(result, "스레드 업데이트 요청이 취소되었습니다");

            setSelectedTab(threadId);
            closeDialog();
        } catch (e) {
            if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
                console.log("스레드 업데이트 요청이 취소되었습니다. 이후 로직을 중단합니다.");
            } else {
                console.error("스레드 업데이트 중 오류 발생:", e);
            }
        } finally {
            disableActionPending("editGroupName");
        }
    };

    const EmptyPrivateThreadComments = () => {
        return (
            <Box height={146} sx={{ px: 1.5, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Stack direction={"column"} gap={1}>
                    <Typography variant={"body1_700"} color={theme.palette.greySecondary.main}>
                        {t("common::msg::There are no private comments.")}
                    </Typography>
                    <Typography variant={"body3_500"} color={theme.palette.greySecondary[600]}>
                        {t(`common::msg::Use the "Create private comment" button Make it.`)}
                    </Typography>
                </Stack>
            </Box>
        );
    };

    const totalUploadingFilesCount = useMemo(() => {
        return uploadQueue[UPLOAD_LOCATION.UPLOAD_COMMENT_ATTACHMENT]
            .filter((queue) => queue.status === "UPLOADING")
            .reduce((acc, queue) => {
                if (queue.data.threadId === selectedThreadId) {
                    return acc + queue.files?.length;
                }
                return acc;
            }, 0);
    }, [uploadQueue, selectedThreadId]);

    const uploadedFilesCount = useMemo(() => {
        return uploadQueue[UPLOAD_LOCATION.UPLOAD_COMMENT_ATTACHMENT]
            .filter((queue) => queue.status === "UPLOADING")
            .reduce((acc, queue) => {
                if (queue.data.threadId === selectedThreadId) {
                    return queue.files?.filter((file) => file?.status?.status === "COMPLETE")?.length + acc;
                }
                return acc;
            }, 0);
    }, [uploadQueue, selectedThreadId]);

    const getCardComments = useCallback(() => {
        let dividerCount = 0;

        const uploadingComentsIds = uploadingComments?.[selectedThreadId]?.map((comment) => comment.id);
        const nonUploadingComments = comments.data?.results?.filter(
            (comment) => !uploadingComentsIds?.includes(comment.id),
        );

        return nonUploadingComments?.map((comment, index) => (
            <>
                {index > 0 &&
                    compareDates(comments.data?.results[index - 1].created.at, comment.created.at) &&
                    makeDateDivider(comments.data?.results[index - 1], dividerCount++)}
                <CardComments
                    key={`common-comment-${comment?.id}`}
                    domain={domain}
                    data={comment}
                    ref={comment.id === linkParams?.commentId ? linkCommentRef : null}
                    isUploading={comment.isUploading}
                    resourceId={resourceId}
                    isCustomMenu={isCustomMenu}
                    isCms={isCms}
                    projectId={projectId}
                />
                {index === comments.data?.results?.length - 1 && makeDateDivider(comment, dividerCount++)}
            </>
        ));
    }, [comments.data?.results, linkParams?.commentId, domain, resourceId, uploadingComments, selectedThreadId]);

    const isThreadEditable = useMemo(() => {
        return getSelectedTabInfo(selectedTab)?.created?.by?.id === stage?.user?.id && COMMENTS_PERMISSIONS.EDIT;
    }, [getSelectedTabInfo, stage?.user?.id, COMMENTS_PERMISSIONS]);

    useEffect(() => {
        const initializeThread = async () => {
            const threadId = getSelectedTabInfo(selectedTab)?.id;
            dispatch(updateSelectedThreadId(threadId));
            if (!threadId) {
                try {
                    let nextToken = null;
                    let members = [];
                    do {
                        const params = {
                            limit: 100,
                            siteAccess: userSiteAccess,
                        };
                        if (compareVersions(stage.version, VERSION_CM_12797_GET_USERS_WITH_ACTION_GROUP_IDS) >= 0) {
                            params.actionGroupIds = "Project:Comments:Read";
                        }

                        if (nextToken) params.nextToken = nextToken;
                        const response = await getProjectUsersAPI(
                            stage.id,
                            stage.endpoint,
                            projectId ?? project.id,
                            params,
                        );
                        nextToken = response.data?.nextToken;
                        members = [...members, ...(response.data?.results || [])];
                    } while (nextToken);

                    const _addedUsers = addedUsers.filter((user) => !members.some((u) => u.id === user.id)) ?? [];
                    if (_addedUsers.length > 0) {
                        const results = _addedUsers.concat(members);
                        const data = { results, totalCount: results.length };
                        setDefaultMembers(data);
                    } else {
                        setDefaultMembers({ results: members, totalCount: members.length });
                    }
                } catch (e) {
                    console.error(e);
                }
            }

            // 모든 비동기 작업이 완료된 후 최종 상태 체크
            if (hidden || (linkParams.isLinkFromEmail && !isLinkCommentUsed)) return;
            onHandleRefresh();
        };

        initializeThread();
    }, [selectedTab, userSiteAccess, projectId, project]);

    useEffect(() => {
        let date = new Date();
        timeOffsetRef.current = date.getTimezoneOffset() * 60000;
    }, []);

    useEffect(() => {
        if (!linkParams.isLinkFromEmail || domain !== linkParams.domain || isLinkCommentUsed) return;

        const init = async () => {
            dispatch(resetComments());
            setHasScrolledToLinkComment(false);
            setMountPending(true);
            const freshData = await dispatch(getThreads(projectId, { domain, resourceId }));
            initCommentsFromLink(freshData?.data);
        };
        init();
    }, [location.search, linkParams, isLinkCommentUsed, domain, projectId, resourceId, dispatch, initCommentsFromLink]);

    useEffect(() => {
        if (!resourceData?.results || !mountPending || (linkParams?.isLinkFromEmail && !isLinkCommentUsed)) {
            return;
        } else if (!resourceData.results.length || !publicThreadId) {
            setMountPending(false);
            return;
        }
        if (publicThreadId) setSelectedTab(publicThreadId);
        initComments();
    }, [resourceData?.results]);

    useEffect(() => {
        if (!comments.data?.results) return;
        const selectedThreadInfo = getSelectedTabInfo(selectedTab);
        if (!selectedThreadId && selectedThreadInfo) {
            dispatch(updateSelectedThreadId(selectedThreadInfo.id));
        }
    }, [comments]);

    useEffect(() => {
        if (comments?.prevPending) return;
        if (prevScrollHeight > 0)
            scrollDivRef.current?.scrollTo(0, scrollDivRef.current?.scrollHeight - prevScrollHeight);
    }, [comments.prevPending]);

    useEffect(() => {
        if (resourceData?.results?.length) return;

        const loadThreads = async () => {
            const signal = getNewController("getThreads");

            try {
                const result = await dispatch(
                    getThreads(
                        projectId,
                        {
                            domain,
                            resourceId,
                        },
                        signal,
                    ),
                );
                handleApiResult(result);
            } catch (error) {
                if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                    console.log("스레드 로드 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                } else {
                    console.error("스레드 로드 중 오류 발생:", error);
                }
            }
        };

        loadThreads();
    }, []);

    useEffect(() => {
        if (
            !comments?.data?.prevToken ||
            comments?.prevPending ||
            initPending ||
            comments?.firstLoading ||
            !resourceData?.results
        )
            return;

        const selectedThread = getSelectedTabInfo(selectedTab);

        const fetchPrevComments = async () => {
            if (prevInView) {
                setPrevScrollHeight(scrollDivRef.current?.scrollHeight);
                const signal = getNewController("prevComments");

                try {
                    const result = await dispatch(
                        getComments(
                            projectId,
                            selectedThread?.id,
                            {
                                nextToken: comments?.data?.prevToken,
                                orderType: "DESC",
                            },
                            signal,
                        ),
                    );
                    handleApiResult(result);
                } catch (error) {
                    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                        console.log("이전 댓글 불러오기 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                    } else {
                        console.error("이전 댓글 불러오기 중 오류 발생:", error);
                    }
                }
            }
        };

        fetchPrevComments();
    }, [prevInView]);

    useEffect(() => {
        if (!comments?.data?.nextToken || comments?.nextPending || !resourceData?.results) return;

        const selectedThread = getSelectedTabInfo(selectedTab);

        const fetchNextComments = async () => {
            if (nextInView) {
                const signal = getNewController("nextComments");

                try {
                    const result = await dispatch(
                        getComments(
                            projectId,
                            selectedThread?.id,
                            {
                                nextToken: comments?.data?.nextToken,
                                orderType: "ASC",
                            },
                            signal,
                        ),
                    );
                    handleApiResult(result);
                } catch (error) {
                    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
                        console.log("다음 댓글 불러오기 요청이 취소되었습니다. 이후 로직을 중단합니다.");
                    } else {
                        console.error("다음 댓글 불러오기 중 오류 발생:", error);
                    }
                }
            }
        };

        fetchNextComments();
    }, [nextInView]);

    useEffect(() => {
        const handleScroll = (e) => {
            setScrollYPosition(e.target.scrollTop);
        };

        if (!hidden && !scrollDivRef.current) {
            scrollDivRef.current?.scrollTo(0, scrollYPosition);
            scrollDivRef.current?.addEventListener("scroll", handleScroll);
            dateDividerRef.current = 0;
        }

        return () => {
            if (hidden) {
                scrollDivRef.current?.removeEventListener("scroll", handleScroll);
                setScrollYPosition(0);
            }
        };
    }, [hidden]);

    useEffect(() => {
        // NOTE: 댓글 새로고침 시 스크롤을 맨 아래로 이동
        if (initPending || !comments?.data?.results?.length || (linkParams.isLinkFromEmail && !isLinkCommentUsed))
            return;
        scrollDivRef.current?.scrollTo(0, scrollDivRef.current?.scrollHeight);
    }, [initPending]);

    useEffect(() => {
        if (mountPending || !comments?.data?.results?.length) return;
        scrollDivRef.current?.scrollTo(0, scrollDivRef.current?.scrollHeight);
    }, [mountPending]);

    useEffect(() => {
        if (!linkParams.isLinkFromEmail || hasScrolledToLinkComment) return;
        if (!comments?.data?.results?.length) return;
        linkCommentRef?.current?.scrollIntoView({ behavior: "instant", block: "center" });
        setHasScrolledToLinkComment(true);
    }, [mountPending]);

    // 컴포넌트 언마운트 시 진행 중인 모든 요청 취소
    useEffect(() => {
        return () => {
            abortAll();
        };
    }, [abortAll]);

    return (
        <Grid hidden={hidden} container flexDirection={"column"} sx={{ height: "100%" }}>
            <Box sx={{ backgroundColor: alpha(theme.palette.background.default, 0.6) }}>
                <Grid
                    container
                    direction={"row"}
                    p={"6px 12px"}
                    sx={{
                        backgroundColor: alpha(theme.palette.common.white, 0.6),
                        height: "44px",
                        borderBottom: `1px solid ${theme.palette.greySecondary.A100}`,
                    }}
                >
                    <Grid item flex={1} gap={1.5} alignItems={"center"} sx={{ display: "flex" }}>
                        <Grid item>
                            <IconReply size={20} htmlColor={theme.palette.success.main} />
                        </Grid>
                        <Grid item flex={1}>
                            <Typography variant={`subtitle1_500`} color={theme.palette.greySecondary.main}>
                                {t("common::label::Comment", "Comment")}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item flex={0} sx={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title={t("common::label::Refresh", "Refresh")}>
                            <IconButton
                                circled
                                disabled={initPending || resourceData?.results?.length === 0}
                                onClick={onHandleRefresh}
                            >
                                <IconRefresh size={16} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>
            {/* Tabs View */}
            <Grid
                container
                flexDirection={"row"}
                alignItems={"center"}
                flexWrap={"nowrap"}
                sx={{ borderBottom: `1px solid ${theme.palette.greySecondary.A100}` }}
            >
                <Grid item xs zeroMinWidth>
                    <Tabs
                        value={getSelectedTabInfo(selectedTab)?.id}
                        items={tabs}
                        onChangeTab={onHandleChangeTab}
                        TabIndicatorProps={{
                            style: {
                                backgroundColor: getSelectedTabInfo(selectedTab)?.isPublic
                                    ? theme.palette.primary.main
                                    : theme.palette.warning.main,
                            },
                        }}
                        indicatorPosition={"bottom"}
                        isBordered={false}
                        textColor={"secondary"}
                        variant={"scrollable"}
                        scrollButtons={"auto"}
                        scrollButtonPosition={"right"}
                        divider={
                            <Divider
                                orientation={"vertical"}
                                color={theme.palette.greySecondary.A100}
                                sx={{ height: "20px", mx: 1 }}
                            />
                        }
                        sx={{
                            ".MuiTab-root": {
                                height: theme.spacing(5),
                            },
                            ".MuiTabScrollButton-root.MuiTabScrollButton-horizontal": {
                                border: 0,
                            },
                        }}
                    />
                </Grid>
                <Divider flexItem orientation={"vertical"} sx={{ borderColor: theme.palette.greySecondary.A100 }} />
                <Grid
                    item
                    sx={{
                        p: 0.5,
                        backgroundColor: threadPopperActive
                            ? theme.palette.greySecondary.main
                            : theme.palette.common.white,
                    }}
                    ref={groupsViewRef}
                >
                    <Tooltip
                        title={t(
                            `common::label::${threadPopperActive ? "Close" : "View private comments"}`,
                            threadPopperActive ? "Close" : "View private comments",
                        )}
                    >
                        <IconButton
                            circled
                            onClick={threadPopperToggle}
                            sx={{
                                "&:hover": {
                                    backgroundColor: threadPopperActive
                                        ? alpha(theme.palette.common.white, 0.08)
                                        : undefined,
                                },
                            }}
                        >
                            {threadPopperActive ? (
                                <IconClose size={16} htmlColor={theme.palette.common.white} />
                            ) : (
                                <IconAllView size={16} />
                            )}
                        </IconButton>
                    </Tooltip>
                </Grid>
                <PopperPrivateThreads
                    open={threadPopperActive}
                    width={314}
                    data={resourceData?.results?.filter((el) => el.type === "PRIVATE")}
                    values={getLocalStoragePrivateThreads(resourceData)?.privateThreadIds ?? []}
                    onChange={onHandleCheckPrivateThreads}
                    onClickActionButton={() => openDialog("createGroup")}
                    onClose={() => {
                        threadPopperToggle();
                    }}
                    anchorEl={groupsViewRef.current}
                    placement={"bottom-start"}
                    noDataComments={<EmptyPrivateThreadComments />}
                />
            </Grid>
            {/*Members View */}
            <Grid
                container
                p={"6px 8px"}
                flexDirection={"row"}
                justifyContent={"flex-end"}
                alignItems={"center"}
                sx={{ borderBottom: `1px solid ${theme.palette.greySecondary.A100}` }}
            >
                <Grid item p={"0 12px"}>
                    <Typography variant={"body3_500"} color={theme.palette.greySecondary[500]}>
                        {t("common::label::member of comment", "member of comment")}
                    </Typography>
                </Grid>
                <Grid item>
                    <GroupedAvatar members={threadInfoWithMembers.members?.results?.slice(0, 4)} />
                </Grid>
                <Grid item p={"0 8px"} onClick={() => openDialog("editGroupMembers")}>
                    <Button
                        color={"primary"}
                        variant={"text"}
                        sx={{ minWidth: "24px", height: "24px", padding: "0px" }}
                    >
                        <Typography variant={"body4_700"} color={theme.palette.primary.main}>
                            {threadInfoWithMembers.members?.totalCount ?? 0}
                        </Typography>
                    </Button>
                </Grid>
                {!getSelectedTabInfo(selectedTab)?.isPublic && (
                    <Grid item>
                        <Menu
                            menuWidth={160}
                            placement={"bottom"}
                            type={"icon"}
                            label={<IconMoreVert size={16} />}
                            color={"inherit"}
                            size={"medium"}
                            list={[
                                {
                                    label: t(`common::label::Change name`, "Change name"),
                                    onClick: (e) => {
                                        openDialog("editGroupName");
                                    },
                                    disabled: !isThreadEditable,
                                },
                                {
                                    label: (
                                        <Typography
                                            color={
                                                !isThreadEditable
                                                    ? alpha(theme.palette.error.main, 0.5)
                                                    : theme.palette.error.main
                                            }
                                        >
                                            {t(`common::label::Delete private comment`, "Delete private comment")}
                                        </Typography>
                                    ),
                                    onClick: (e) => {
                                        openDialog("deleteGroup");
                                    },
                                    divider: true,
                                    disabled: !isThreadEditable,
                                },
                            ]}
                        />
                    </Grid>
                )}
            </Grid>
            {totalUploadingFilesCount > 0 && (
                <Box
                    width={400}
                    backgroundColor={"secondary.main"}
                    color={"secondary.contrastText"}
                    borderRadius={1.125}
                    sx={{
                        position: `absolute`,
                        top: "140px",
                        left: `50%`,
                        ml: `-200px`,
                        // visibility: isShow ? `visible`: `hidden`,
                        zIndex: 101,
                    }}
                >
                    <Stack px={2.5} py={1}>
                        <Typography>
                            {/*{queue.length}*/}
                            {t(`common::label::Uploading files`, `Uploading files`)} ({uploadedFilesCount} /{" "}
                            {totalUploadingFilesCount}
                            )...
                        </Typography>
                        <Grid container alignItems={"center"} flexWrap={"nowrap"} gap={1} mt={0.5}>
                            <Grid item xs>
                                <LinearProgress
                                    gradient
                                    variant="determinate"
                                    value={(uploadedFilesCount / totalUploadingFilesCount) * 100}
                                />
                            </Grid>
                            <Grid item sx={{ color: theme.palette.secondary.contrastText }}>
                                <Tooltip title={t(`common::label::Cancel Update`, `Cancel Update`)}>
                                    <IconButton color={"inherit"} circled onClick={() => {}}>
                                        <IconCancel size={12} />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Stack>
                </Box>
            )}
            {/* Comments View */}
            <Grid
                item
                flex={1}
                sx={{
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    scrollbarGutter: "stable",
                    backgroundColor: alpha(theme.palette.greySecondary["A100"], 0.48),
                }}
                ref={scrollDivRef}
            >
                <Grid
                    container
                    flexDirection={"column-reverse"}
                    sx={{
                        minHeight: 1,
                        gap: 0.75,
                        py: 0.75,
                        pl: 2.5,
                        pr: 0.75,
                        flexWrap: "nowrap",
                    }}
                >
                    {/*column-reverse를 사용하여 실제 렌더링은 제일 아래의 CardComments->Badge->map 순서로 렌더링됩니다.*/}
                    {!mountPending && (
                        <>
                            {uploadingComments?.[selectedThreadId]?.length > 0 && (
                                <>
                                    {uploadingComments?.[selectedThreadId].map((comment) => (
                                        <CardComments
                                            domain={domain}
                                            key={`domain-comment-${comment?.id}`}
                                            data={comment}
                                            isUploading={true}
                                            projectId={projectId}
                                        />
                                    ))}
                                </>
                            )}
                            {comments?.nextPending ? (
                                <>
                                    <SkeletonComment />
                                </>
                            ) : (
                                <Grid ref={nextTargetRef} hidden={!comments?.data?.nextToken} />
                            )}
                            {getCardComments()}
                            {!comments?.createPending &&
                                !comments?.prevPending &&
                                (!comments.data?.results || comments.data?.results?.length === 0) &&
                                !uploadingComments?.[selectedThreadId]?.length && (
                                    <Stack direction={"column"} sx={{ p: "20px 0" }} gap={1.5}>
                                        <GroupTitleWithIcon
                                            isPublic={getSelectedTabInfo(selectedTab)?.isPublic}
                                            title={getSelectedTabInfo(selectedTab)?.name}
                                            iconSize={24}
                                            fontVariant={"h4"}
                                        />
                                        <GroupDescription isPublic={getSelectedTabInfo(selectedTab)?.isPublic} />
                                    </Stack>
                                )}
                        </>
                    )}
                    {mountPending || comments?.createPending || comments?.prevPending ? (
                        <>
                            {new Array(mountPending || comments?.createPending ? 5 : 1).fill("").map((value, index) => (
                                <SkeletonComment key={`reply-container-skeleton-${index}`} />
                            ))}
                        </>
                    ) : (
                        <Grid ref={prevTargetRef} hidden={!comments?.data?.prevToken} />
                    )}
                </Grid>
            </Grid>
            {dialog === "deleteGroup" && (
                <DialogDeleteGroup
                    data={getSelectedTabInfo(selectedTab)}
                    isLoading={getActionPending("deleteGroup")}
                    onSubmit={onDeletePrivateThread}
                    onClose={closeDialog}
                ></DialogDeleteGroup>
            )}
            {dialog === "createGroup" && (
                <DialogCreateGroup
                    domain={domain}
                    isLoading={getActionPending("createGroup")}
                    onSubmit={onCreatePrivateThread}
                    onClose={closeDialog}
                    userSiteAccess={userSiteAccess}
                    projectId={projectId}
                    addedUsers={addedUsers}
                ></DialogCreateGroup>
            )}
            {dialog === "editGroupMembers" && (
                <DialogEditGroupMembers
                    data={threadInfoWithMembers}
                    allMembers={defaultMembers?.results ?? []}
                    isCustomMenu={isCustomMenu}
                    isCms={isCms}
                    onSubmit={onUpdateThreadMembers}
                    isLoading={getActionPending("editGroupMembers")}
                    onClose={closeDialog}
                    userSiteAccess={userSiteAccess}
                    projectId={projectId}
                ></DialogEditGroupMembers>
            )}
            {dialog === "editGroupName" && (
                <DialogEditGroupName
                    data={getSelectedTabInfo(selectedTab)}
                    isLoading={getActionPending("editGroupName")}
                    onSubmit={onUpdatePrivateThread}
                    onClose={closeDialog}
                ></DialogEditGroupName>
            )}
        </Grid>
    );
};

export default React.memo(ReplyContainer);
